import { Directive, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, fromEvent, Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, skipWhile, takeUntil, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { deepClone } from '@utils/object-utils';
import { getObjectDiff } from '@utils/object-utils';
import { AbstractEntityState, EditMode } from './abstract-entity.state';
import { AbstractEntityDataProvider } from '../entity-data-provider/abstract-entity.data-provider';
import { AbstractIdentifiable } from './abstract-entity';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { LoggingService } from '@logging/logging.service';
import { AbstractDataset } from '@entity-framework/datasets/abstract-dataset';
import { ErrorHandlerService } from '@utils/error-handler.service';
import { FileDownloadService } from '@utils/file-download.service';
import { FullScreenLoaderService } from '@design-system/loaders/fullscreen-loader.service';

export interface EntityStateServiceOptions {
  autoFetch?: boolean;
  childEntity?: boolean;
  runValidatorsOnFormBind?: boolean;
}

@UntilDestroy()
@Directive()
export abstract class AbstractEntityStateService<TEntity extends AbstractIdentifiable> implements OnDestroy {
  
  // State management
  private readonly stateSubject = new BehaviorSubject<AbstractEntityState<TEntity>>({ 
    editing: false, 
    inFlight: true,
    entity: undefined,
    entitySnapshot: undefined,
    serverValidationErrors: undefined
  });

  // Form management
  public formGroup?: UntypedFormGroup;
  private formGroupSubject = new BehaviorSubject<UntypedFormGroup | undefined>(undefined);
  private initialFormValue: any;
  private destroy$ = new Subject<void>();
  private dirtySubject = new BehaviorSubject<boolean>(false);
  
  // File download service (optional)
  protected fileDownloadService?: FileDownloadService;

  private readonly fullScreenLoaderService = inject(FullScreenLoaderService);

  // Public observables
  readonly state$ = this.stateSubject.asObservable();
  readonly entity$ = this.state$.pipe(map(state => state.entity));
  readonly editing$ = this.state$.pipe(map(state => state.editing));
  readonly isDirty$ = this.dirtySubject.asObservable();
  readonly formGroup$ = this.formGroupSubject.asObservable().pipe(
    skipWhile(formGroup => !formGroup),
    map(formGroup => formGroup as UntypedFormGroup)
  );
  readonly serverValidationErrors$ = this.state$.pipe(map(state => state.serverValidationErrors));

  // Also expose a synchronous getter for server validation errors
  get serverValidationErrors(): any {
    return this.currentState.serverValidationErrors;
  }

  // Event handlers
  onCreate: (entity: TEntity) => void = () => {};
  onFetch: (entity: TEntity | TEntity[]) => void = () => {};
  onUpdate: (entity: TEntity) => void = () => {};
  onDelete: () => void = () => {};
  onCreateMany: (entities: TEntity[]) => void = () => {};

  protected constructor(
    protected storeName: string,
    protected dataProvider: AbstractEntityDataProvider<TEntity>,
    protected route: ActivatedRoute, 
    protected errorHandler: ErrorHandlerService,
    protected messaging: OnScreenMessagingService,
    protected logger: LoggingService,
    protected spawn?: () => TEntity,
    protected options: EntityStateServiceOptions = {
      autoFetch: true,
      childEntity: false,
      runValidatorsOnFormBind: false
    },
    protected fileDownload?: FileDownloadService
  ) {
    this.fileDownloadService = fileDownload;
    this.watchDirtyUnload();
    this.watchDirtyNavigate();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
    this.formGroupSubject.complete();
    this.dirtySubject.complete();
  }

  /**
   * Clear all state for this entity service without destroying the service instance.
   * Useful when a short-lived component (e.g. a modal) wants to ensure data does not persist after close.
   */
  public resetState(): void {
    // Reset form bindings
    this.formGroup = undefined;
    this.formGroupSubject.next(undefined);
    this.initialFormValue = undefined as any;

    // Reset core state
    this.updateState({
      editing: false,
      inFlight: false,
      entity: undefined,
      entitySnapshot: undefined,
      serverValidationErrors: undefined,
    });

    //Reset parent Path
    this.dataProvider.setParentPath('');

    // Reset dirty flag
    this.dirtySubject.next(false);
  }

  // State management methods
  private get currentState(): AbstractEntityState<TEntity> {
    return this.stateSubject.getValue();
  }

  private updateState(newState: Partial<AbstractEntityState<TEntity>>): void {
    this.stateSubject.next({ ...this.currentState, ...newState });
  }

  set validationErrors(validationErrors: any) {
    this.stateSubject.next({ ...this.currentState, serverValidationErrors: validationErrors });
  }

  //Only use this for overriding state, not form interactions
  set entity(entity:TEntity) {
    this.stateSubject.next({  ...this.currentState, entity: entity})
  }

  set inFlight(inFlight : boolean) {
    this.stateSubject.next({  ...this.currentState, inFlight : inFlight})
  }

  validationErrors$: Observable<string | undefined> = this.serverValidationErrors$.pipe(
    map(errors => {
      if (Array.isArray(errors)) {
        return errors.join(", ");
      } else if (errors && typeof errors === 'object') {
        return Object.values(errors).flat().join(", ");
      }
      return errors;
    })
  );

  inFlight$ = this.stateSubject.pipe(
    map(state => state.inFlight ?? false)
  );

  initialiseForm(createForm: (entity: TEntity | undefined) => UntypedFormGroup, id?: any) {
    this.dataProvider.setParentPath(id)
    this.toggleEditing(!!id);
    if(id){
      this.fetchEntityByIdAndBindFormGroup(id, createForm);  
    }
    else{
      this.bindFormGroup(createForm);
    }
  }

  // Form binding and management
  bindFormGroup(createForm: (entity: TEntity | undefined) => UntypedFormGroup): void {
    this.inFlight = true
    const entity = this.currentState.entity;
    this.logger.trace(`(${this.storeName}) AbstractEntityStateService: form binding to:`, entity);

    // Clean up previous form subscriptions
    if (this.formGroup) {
      this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe();
    }

    // Create and set up new form
    this.formGroup = createForm(entity);

    this.formGroupSubject.next(this.formGroup);
    this.initialFormValue = this.formGroup.value;

    // Set up form validation if needed
    if (this.options.runValidatorsOnFormBind) {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }

    // Watch for form changes
    this.formGroup.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        const prevEntity = this.currentState.entity || {};
        const merged = { ...prevEntity, ...value };
        this.updateState({ entity: merged as TEntity });

        this.checkDirtyState();
      });
  
    // Take a snapshot of the initial entity state
    this.updateState({ 
      entitySnapshot: entity 
    });

    this.inFlight = false;
  }

  fetchEntityByIdAndBindFormGroup(
    id: string | number,
    createForm: (entity: TEntity | undefined) => UntypedFormGroup,
    options: { suppressToast?: boolean } = {}
  ) {
      this.fetchEntity$(id, options).subscribe((_entity) => {
        this.bindFormGroup(createForm)
      });
  }

  fetchEntity(id?: string | number, options: { suppressToast?: boolean } = {}): void {
    this.fetchEntity$(id, options).pipe(untilDestroyed(this)).subscribe((_entity) => { });
  }

  fetchEntity$(
    id?: string | number,
    options: { suppressToast?: boolean } = {}
  )
  {
    this.inFlight = true;

    if(id)
      this.dataProvider.setParentPath(id);

    return this.dataProvider.read$().pipe(
      map((response: TEntity) => {
        if (!response) {
          throw new Error('Entity not found');
        }
        return response as TEntity;
      }),
      tap(entity => {
        this.updateState({ 
          entity: entity,
          entitySnapshot: entity,
          serverValidationErrors: undefined
        });
        this.onFetch(entity);
      }),  
      catchError(err => {
        const validationErrors = this.errorHandler.extractValidationErrors(err);
        if (validationErrors) {
          this.updateState({ serverValidationErrors: validationErrors });
        }
        return this.errorHandler.handleGetErrors(err, options, this.storeName);
      }),
      finalize(() => this.inFlight = false),
    )
  }

  /**
   * Fetch all entities using the data provider.
   */
  fetchAllEntities$(options: { suppressToast?: boolean } = {}): Observable<TEntity[]> {
    this.inFlight = true;
    return this.dataProvider.readAll$().pipe(
      tap(entities => {
        this.logger.trace(`Fetched all entities for ${this.storeName}:`, entities);
      }),
      catchError(err => {
        const validationErrors = this.errorHandler.extractValidationErrors(err);
        if (validationErrors) {
          this.updateState({ serverValidationErrors: validationErrors });
        }
        return this.errorHandler.handleGetErrors(err, options, this.storeName);
      }),
      finalize(() => this.inFlight = false)
    );
  }

  protected processFileDownload(blob: Blob, fileName: string, successMessage?: string): void {
    if (blob && blob.size > 0) {
      if (this.fileDownloadService) {
        this.fileDownloadService.downloadFile(blob, fileName);
        this.messaging.success(successMessage || 'File downloaded successfully - please check your downloads folder.');
      }
    } else {
      this.messaging.warn('No data found for your search criteria.');
    }
  }

  private handleDownloadError(err: any, endPoint?: string): Observable<Blob> {
    this.logger.error(`Error downloading from ${endPoint || 'endpoint'}`, err);
    this.messaging.warn('Error downloading file. Please try again.');
    return this.errorHandler.handleGetErrors(err, {}, this.storeName);
  }

  protected downloadAndProcessFile(
    params: any, 
    fileName: string, 
    endPoint?: string,
    successMessage?: string
  ): void {
    this.inFlight = true;
    this.fullScreenLoaderService.show();
    this.dataProvider.download$(params, endPoint).pipe(
      tap(blob => this.processFileDownload(blob, fileName, successMessage)),
      catchError(err => this.handleDownloadError(err, endPoint)),
      finalize(() => {
        this.inFlight = false;
        this.fullScreenLoaderService.hide();
      })
    ).subscribe();
  }

  protected postDownloadAndProcessFile(
    body: any, 
    fileName: string, 
    endPoint?: string,
    successMessage?: string
  ): void {
    this.inFlight = true;
    this.fullScreenLoaderService.show();
    this.dataProvider.postDownload$(body, endPoint).pipe(
      tap(blob => this.processFileDownload(blob, fileName, successMessage)),
      catchError(err => this.handleDownloadError(err, endPoint)),
      finalize(() => {
        this.inFlight = false;
        this.fullScreenLoaderService.hide();
      })
    ).subscribe();
  }

  private checkDirtyState(): void {
    if (!this.formGroup) return;

    // Compare current form value with initial value
    const currentValue = this.formGroup.value;
    
    // Quick reference check first
    if (currentValue === this.initialFormValue) {
      this.dirtySubject.next(false);
      return;
    }

    // Compare values using JSON.stringify for objects/arrays and direct comparison for primitives
    const valuesChanged = Object.keys(currentValue).some(key => {
      const curr = currentValue[key];
      const init = this.initialFormValue?.[key];
      
      // Use JSON.stringify for objects and arrays, direct comparison for primitives
      return typeof curr === 'object' && curr !== null ? 
        JSON.stringify(curr) !== JSON.stringify(init) : 
        curr !== init;
    });

    if (valuesChanged) {
      this.logger.log('Form state changes:', {
        current: currentValue,
        initial: this.initialFormValue,
        diff: getObjectDiff(currentValue, this.initialFormValue)
      });
    }

    this.dirtySubject.next(valuesChanged);
  }

  formDirty(): boolean {
    return this.dirtySubject.getValue();
  }

  // Entity operations
  persist$(): Observable<TEntity> {
    this.inFlight = true;

    if (!this.formValid()) {
      this.inFlight = false;
       throw new Error('Form is not valid - if using a form ensure you validate it before trying to persist.');
    }

    const entity = this.currentState.entity;
    
    if (!entity) {
      this.inFlight = false;
      throw new Error('Cannot persist undefined entity');
    }

    const creating = this.getEditMode(this.currentState.entity) === 'Creating';
    const update$ = creating ? this.createEntity$(entity) : this.updateEntity$(entity);
    return update$.pipe(
      catchError(err => {
        if(err.status === 400) {
          const validationErrors = this.errorHandler.extractValidationErrors(err);
          if (validationErrors) {
            this.updateState({ serverValidationErrors: validationErrors });
          }
        }
        return this.errorHandler.handlePersistError(err, creating, this.storeName);
      }),
      tap({
        next: result => {
          // Reset dirty state and take new snapshot
          if (this.formGroup) {
            this.formGroup.markAsPristine();
            this.initialFormValue = this.formGroup.value;
          }
          this.updateState({ 
            entity: result,
            entitySnapshot: result,
            serverValidationErrors: undefined 
          });
          this.dirtySubject.next(false);
        }
      }),
      finalize(() => this.inFlight = false)
    );
  }

  public createDataset<T>(dataSelector: (data: any) => T[], 
    getValue: (item: T) => string, 
    getLabel: (item: T) => string): Observable<AbstractDataset<T, string>> {
      return this.entity$.pipe(map(filterData => {
        return new (class extends AbstractDataset<T, string> {
          getValue(item: T) { return getValue(item); }
          getLabel(item: T) { return getLabel(item); }
        })(Array.from(filterData ? dataSelector(filterData) : [])); 
    }));
  }

  protected createEntity$(entity: TEntity): Observable<TEntity> {
    entity = this.transformEntityForSubmit(entity);
    return this.dataProvider.create$(entity).pipe(
      map((response: unknown) => response as TEntity),
      tap(created => {
        this.updateState({ entity: created });
        this.onCreate(created);
      })
    );
  }

  protected updateEntity$(entity: TEntity): Observable<TEntity> {
    entity = this.transformEntityForSubmit(entity);
    return this.dataProvider.update$(entity).pipe(
      map((response: unknown) => response as TEntity),
      tap(updated => {
        this.updateState({ entity: updated });
        this.onUpdate(updated);
      })
    );
  }

  //Interceptable to allow model transformation prior to update or create method
  transformEntityForSubmit(entity: TEntity): TEntity {
    return entity;
  }

  delete$(): Observable<void> {
    const entity = this.currentState.entity;
    if (!entity?.id) {
      return throwError(() => new Error('Cannot delete entity without ID'));
    }
    return this.deleteUsing$(this.dataProvider.delete$());
  }

  deleteById$(id: string): Observable<void> {
    return this.deleteUsing$(this.dataProvider.deleteById$(id));
  }

  deleteUsing$(deleteOperation: Observable<void>): Observable<void> {
    this.inFlight = true;
    return deleteOperation.pipe(
      tap(() => {
        this.updateState({ 
          entity: undefined,
          entitySnapshot: undefined,
          serverValidationErrors: undefined
        });
       
        this.dataProvider.setParentPath('');
        this.onDelete();
      }),
      catchError(err => {
        const validationErrors = this.errorHandler.extractValidationErrors(err);
        if (validationErrors) {
          this.updateState({ serverValidationErrors: validationErrors });
        }
        return this.errorHandler.handleDeleteError(err, this.storeName);
      }),
      finalize(() => this.inFlight = false)
    );
  }

  /**
   * Create multiple entities in a single request.
   */
  createEntities$(entities: TEntity[]): Observable<TEntity[]> {
    this.inFlight = true;
    return this.dataProvider.createMany$(entities).pipe(
      map((response: unknown) => response as TEntity[]),
      tap(createdEntities => {
        this.onCreateMany(createdEntities);
        // Optionally update state here if desired
      }),
      catchError(err => {
        const validationErrors = this.errorHandler.extractValidationErrors(err);
        if (validationErrors) {
          this.updateState({ serverValidationErrors: validationErrors });
        }
        return this.errorHandler.handlePersistError(err, true, this.storeName);
      }),
      finalize(() => this.inFlight = false)
    );
  }
  
  // Form validation
  formValid(): boolean {
    if (!this.formGroup || this.formGroup.valid) return true;
    
    this.logger.trace('INVALID FORM ', this.formGroup);
    this.touchAllFormFields(this.formGroup);
    return false;
  }

  private touchAllFormFields(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl || control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity();
      } else if (control instanceof UntypedFormGroup || control instanceof FormGroup) {
        this.touchAllFormFields(control);
      } else if (control instanceof UntypedFormArray || control instanceof FormArray) {
        control.controls.forEach(c => this.touchAllFormFields(c as UntypedFormGroup));
      }
    });
  }

  // Edit mode management
  protected getEditMode(entity?: TEntity): EditMode {
    return !entity || !entity.id ? 'Creating' : 'Updating';
  }

  toggleEditing(toState?: boolean): void {
    const currentEditing = this.currentState.editing;
    if (toState === undefined) toState = !currentEditing;
    if (toState === currentEditing) return;
    
    this.updateState({ editing: toState });
  }

  cancelEditing = (cancelFn?: () => void): void => {
    const cancel = () => {
      if (cancelFn) cancelFn();
      this.revertEntitySnapshot();
    };

    if (this.formDirty()) {
      this.logger.trace('Abstract Entity State: form is dirty: ', this.formGroup);
      this.messaging.confirm('You have made edits and these changes will be lost - are you sure you want to cancel?', cancel);
    } else {
      cancel();
    }
  };

  protected revertEntitySnapshot(): void {
    this.logger.trace('Cancelling: Reverting entity snapshot ', this.currentState.entitySnapshot);
    
    // Reset form to initial state
    if (this.formGroup) {
      this.formGroup.reset(this.initialFormValue);
    }

    // Revert entity to snapshot
    this.updateState({ 
      entity: deepClone(this.currentState.entitySnapshot) 
    });
    
    this.dirtySubject.next(false);
  }

  // Navigation and window event handlers
  private watchDirtyUnload(): void {
    fromEvent(window, 'beforeunload')
      .pipe(untilDestroyed(this))
      .subscribe((event: BeforeUnloadEvent) => {
        if (!this.formDirty()) return;
        
        this.logger.trace(`APP UNLOAD: ${this.storeName} EntityStateService has a dirty form - confirming exit...`);
        event.preventDefault();
        // Modern browsers ignore custom messages and just show a generic one
        event.returnValue = '';
      });
  }

  private watchDirtyNavigate(): void {
    // Implementation for route guard if needed
  }

} 
